// =============================================================================
// Stats Component
// =============================================================================

import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText,
  Calendar,
  AlertTriangle,
  Activity,
  Clock,
  BarChart2,
  TrendingUp,
  Hash,
  ExternalLink,
} from 'lucide-react';
import { cn } from '../lib/utils.js';
import type { App, StatsSummary, TimelineResponse } from '../lib/api.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Button } from './ui/button.js';
import { Select } from './ui/select.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip.js';

// =============================================================================
// Types
// =============================================================================

export interface StatsProps {
  /** Available apps */
  apps: App[];
  /** Selected app ID */
  selectedAppId: string | null;
  /** Callback when app is selected */
  onSelectApp: (appId: string | null) => void;
  /** Stats summary data */
  summary: StatsSummary | null;
  /** Timeline data */
  timeline: TimelineResponse | null;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error message */
  error?: string | null;
  /** Time range start */
  from: string;
  /** Time range end */
  to: string;
  /** Callback when time range changes */
  onTimeRangeChange: (from: string, to: string) => void;
}

// =============================================================================
// Time Range Presets
// =============================================================================

const TIME_RANGES = [
  { label: 'Last hour', hours: 1 },
  { label: 'Last 6 hours', hours: 6 },
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 168 },
  { label: 'Last 30 days', hours: 720 },
];

// =============================================================================
// Helper Components
// =============================================================================

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  value: number | string;
  description?: string;
  icon: React.ElementType;
  color: 'blue' | 'purple' | 'green' | 'red' | 'orange';
  isLoading?: boolean;
}) {
  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-400',
    purple: 'bg-purple-500/10 text-purple-400',
    green: 'bg-green-500/10 text-green-400',
    red: 'bg-red-500/10 text-red-400',
    orange: 'bg-orange-500/10 text-orange-400',
  };

  const hoverClasses = {
    blue: 'hover:shadow-[0_4px_20px_rgba(59,130,246,0.1)]',
    purple: 'hover:shadow-[0_4px_20px_rgba(139,92,246,0.1)]',
    green: 'hover:shadow-[0_4px_20px_rgba(34,197,94,0.1)]',
    red: 'hover:shadow-[0_4px_20px_rgba(239,68,68,0.1)]',
    orange: 'hover:shadow-[0_4px_20px_rgba(249,115,22,0.1)]',
  };

  return (
    <Card className={cn('group relative overflow-hidden transition-all duration-300', hoverClasses[color])}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">
          {title}
        </CardTitle>
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', colorClasses[color])}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-9 w-20 animate-pulse rounded bg-[var(--color-muted)]" />
        ) : (
          <div className="text-3xl font-bold tracking-tight text-[var(--color-foreground)]">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
        )}
        {description && (
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

function DistributionBar({
  data,
  colorMap,
}: {
  data: Record<string, number>;
  colorMap: Record<string, string>;
}) {
  const total = Object.values(data).reduce((sum, val) => sum + val, 0);
  if (total === 0) return null;

  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);

  // Calculate which items are visible in bar (>=0.5%) and which are too small
  const visibleInBar = entries.filter(([, value]) => (value / total) * 100 >= 0.5);
  const tooSmallForBar = entries.filter(([, value]) => (value / total) * 100 < 0.5 && value > 0);

  return (
    <div className="space-y-2">
      {/* Bar */}
      <div className="flex h-3 overflow-hidden rounded-full bg-[var(--color-secondary)]">
        {visibleInBar.map(([key, value]) => {
          const percentage = (value / total) * 100;
          return (
            <div
              key={key}
              className={cn('h-full transition-all duration-300', colorMap[key] || 'bg-gray-500')}
              style={{ width: `${percentage}%` }}
              title={`${key}: ${value.toLocaleString()} (${percentage.toFixed(1)}%)`}
            />
          );
        })}
        {/* Show a thin slice for items too small to display normally */}
        {tooSmallForBar.length > 0 && (
          <div
            className="h-full bg-gradient-to-r from-red-500 to-orange-500"
            style={{ minWidth: '4px', width: '4px' }}
            title={tooSmallForBar.map(([key, value]) =>
              `${key}: ${value.toLocaleString()} (<0.5%)`
            ).join('\n')}
          />
        )}
      </div>
      {/* Legend - show all items */}
      <div className="flex flex-wrap gap-3">
        {entries.map(([key, value]) => {
          const percentage = (value / total) * 100;
          const isSmall = percentage < 0.5;
          return (
            <div key={key} className="flex items-center gap-1.5 text-xs">
              <div className={cn('h-2 w-2 rounded-full', colorMap[key] || 'bg-gray-500')} />
              <span className="text-[var(--color-muted-foreground)]">{key}:</span>
              <span className={cn('font-medium', isSmall ? 'text-orange-400' : 'text-[var(--color-foreground)]')}>
                {value.toLocaleString()}
                {isSmall && <span className="text-[10px] ml-1">(&lt;1%)</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function Stats({
  apps,
  selectedAppId,
  onSelectApp,
  summary,
  timeline,
  isLoading = false,
  error,
  from,
  to,
  onTimeRangeChange,
}: StatsProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params
  const urlAppId = searchParams.get('app');
  const urlRange = searchParams.get('range');

  // Initialize selected range from URL or default
  const [selectedRange, setSelectedRange] = React.useState<number>(() => {
    if (urlRange) {
      const parsed = parseInt(urlRange, 10);
      if (TIME_RANGES.some(r => r.hours === parsed)) return parsed;
    }
    return 24;
  });

  // Sync app selection from URL on mount
  React.useEffect(() => {
    if (urlAppId && urlAppId !== selectedAppId && apps.some(a => a.id === urlAppId)) {
      onSelectApp(urlAppId);
    }
  }, [urlAppId, selectedAppId, apps, onSelectApp]);

  // Sync URL when app changes
  React.useEffect(() => {
    if (selectedAppId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('app', selectedAppId);
      newParams.set('range', String(selectedRange));
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedAppId, selectedRange, searchParams, setSearchParams]);

  // Trigger initial data fetch from URL params on mount
  const initialFetchDone = React.useRef(false);
  React.useEffect(() => {
    if (initialFetchDone.current) return;
    if (!apps.length) return;

    // If URL has app param, trigger data fetch with the correct range
    const appIdToUse = urlAppId || selectedAppId || apps[0]?.id;
    if (appIdToUse) {
      const rangeToUse = urlRange ? parseInt(urlRange, 10) : selectedRange;
      const now = new Date();
      const fromDate = new Date(now.getTime() - rangeToUse * 60 * 60 * 1000);

      if (!selectedAppId) {
        onSelectApp(appIdToUse);
      }
      onTimeRangeChange(fromDate.toISOString(), now.toISOString());
      initialFetchDone.current = true;
    }
  }, [apps, urlAppId, urlRange, selectedAppId, selectedRange, onSelectApp, onTimeRangeChange]);

  const handleRangeChange = (hours: number) => {
    setSelectedRange(hours);
    const now = new Date();
    const fromDate = new Date(now.getTime() - hours * 60 * 60 * 1000);
    onTimeRangeChange(fromDate.toISOString(), now.toISOString());
  };

  const levelColors: Record<string, string> = {
    debug: 'bg-gray-500',
    info: 'bg-blue-500',
    warn: 'bg-yellow-500',
    error: 'bg-red-500',
    fatal: 'bg-purple-500',
  };

  const typeColors: Record<string, string> = {
    log: 'bg-slate-500',
    event: 'bg-blue-500',
    timing: 'bg-green-500',
    metric: 'bg-purple-500',
    http: 'bg-orange-500',
    job: 'bg-cyan-500',
    bug_report: 'bg-red-500',
  };

  // Fuzzy group similar messages (e.g., HTTP requests with different timings)
  const groupSimilarMessages = (messages: Array<{ message: string; count: number }>) => {
    if (!messages || messages.length === 0) return [];

    const grouped: Array<{ message: string; count: number; pattern: string }> = [];
    const patterns = new Map<string, { message: string; count: number; examples: string[] }>();

    for (const item of messages) {
      // Normalize message by replacing variable parts with placeholders
      let pattern = item.message
        // Replace UUIDs
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{uuid}')
        // Replace numbers in parentheses like (575ms), (1234)
        .replace(/\(\d+(?:ms|s|m|h)?\)/g, '({time})')
        // Replace timestamps
        .replace(/\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/g, '{timestamp}')
        // Replace standalone numbers that look like IDs or counts
        .replace(/(?<=\s|^)\d{3,}(?=\s|$)/g, '{num}')
        // Replace HTTP status codes pattern
        .replace(/\b\d{3}\b(?=\s+\()/g, '{status}')
        // Replace long hashes/tokens
        .replace(/[a-f0-9]{32,}/gi, '{hash}')
        // Replace URLs with query params
        .replace(/https?:\/\/[^\s]+/g, '{url}')
        // Replace file paths with hashes
        .replace(/_[a-f0-9]{6,}\./gi, '_{hash}.');

      const existing = patterns.get(pattern);
      if (existing) {
        existing.count += item.count;
        if (existing.examples.length < 3) {
          existing.examples.push(item.message);
        }
      } else {
        patterns.set(pattern, {
          message: item.message,
          count: item.count,
          examples: [item.message],
        });
      }
    }

    // Convert to array and sort by count
    return Array.from(patterns.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10)
      .map(([pattern, { message, count, examples }]) => ({
        message: examples.length > 1 ? message.replace(/\(\d+(?:ms|s)?\)/, '(~)') : message,
        count,
        variants: examples.length,
        // Create a regex pattern that will match similar messages
        pattern: examples.length > 1 ? escapeRegexForSearch(pattern) : null,
        originalMessage: message,
      }));
  };

  // Convert pattern placeholders to regex for search
  const escapeRegexForSearch = (pattern: string): string => {
    // Escape special regex chars except our placeholders
    let escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Replace placeholders with regex patterns
    escaped = escaped
      .replace(/\\{uuid\\}/g, '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}')
      .replace(/\\{time\\}/g, '\\d+(?:ms|s|m|h)?')
      .replace(/\\{timestamp\\}/g, '\\d{4}-\\d{2}-\\d{2}[T ]\\d{2}:\\d{2}:\\d{2}')
      .replace(/\\{num\\}/g, '\\d+')
      .replace(/\\{status\\}/g, '\\d{3}')
      .replace(/\\{hash\\}/g, '[a-f0-9]{6,}')
      .replace(/\\{url\\}/g, 'https?://[^\\s]+');
    return escaped;
  };

  // Navigate to logs page with filter
  const handleMessageClick = (item: { message: string; pattern: string | null; originalMessage: string }) => {
    const params = new URLSearchParams();
    if (selectedAppId) {
      params.set('app_id', selectedAppId);
    }
    if (item.pattern) {
      // Use regex for grouped messages
      params.set('regex', item.pattern);
    } else {
      // Use exact match for single messages
      params.set('match', item.originalMessage);
    }
    navigate(`/logs?${params.toString()}`);
  };

  const groupedMessages = React.useMemo(
    () => groupSimilarMessages(summary?.topMessages || []),
    [summary?.topMessages]
  );

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        {/* App Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-[var(--color-muted-foreground)]">App:</label>
          <Select
            className="w-[200px]"
            value={selectedAppId || ''}
            onChange={(e) => onSelectApp(e.target.value || null)}
            options={apps.map((app) => ({ value: app.id, label: app.name }))}
            placeholder="Select an app"
          />
        </div>

        {/* Time Range Buttons */}
        <div className="flex items-center gap-1">
          {TIME_RANGES.map((range) => (
            <Button
              key={range.hours}
              variant={selectedRange === range.hours ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleRangeChange(range.hours)}
            >
              {range.label}
            </Button>
          ))}
        </div>
      </div>

      {/* No App Selected State */}
      {!selectedAppId && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-secondary)]">
              <BarChart2 className="h-6 w-6 text-[var(--color-muted-foreground)]" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--color-foreground)]">
              Select an App
            </h3>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Choose an application to view statistics
            </p>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-red-500/50 bg-red-500/10">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </CardContent>
        </Card>
      )}

      {/* Stats Content */}
      {selectedAppId && !error && (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <StatCard
              title="Total Logs"
              value={summary?.totals.logs ?? 0}
              description="Total entries in time range"
              icon={FileText}
              color="blue"
              isLoading={isLoading}
            />
            <StatCard
              title="Events"
              value={summary?.totals.events ?? 0}
              description="Structured events"
              icon={Activity}
              color="purple"
              isLoading={isLoading}
            />
            <StatCard
              title="Errors"
              value={summary?.totals.errors ?? 0}
              description="Error level logs"
              icon={AlertTriangle}
              color="red"
              isLoading={isLoading}
            />
            <StatCard
              title="Time Range"
              value={`${selectedRange}h`}
              description={summary?.timeRange ? `${new Date(summary.timeRange.from).toLocaleDateString()} - ${new Date(summary.timeRange.to).toLocaleDateString()}` : ''}
              icon={Clock}
              color="green"
              isLoading={isLoading}
            />
          </div>

          {/* Distribution Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* By Level */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                    <TrendingUp className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-[var(--color-foreground)]">By Level</CardTitle>
                    <CardDescription>Log distribution by severity level</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="h-20 animate-pulse rounded bg-[var(--color-muted)]" />
                ) : summary?.byLevel && Object.keys(summary.byLevel).length > 0 ? (
                  <DistributionBar data={summary.byLevel} colorMap={levelColors} />
                ) : (
                  <p className="text-sm text-[var(--color-muted-foreground)]">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* By Type */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
                    <Hash className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <CardTitle className="text-[var(--color-foreground)]">By Type</CardTitle>
                    <CardDescription>Log distribution by type</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="h-20 animate-pulse rounded bg-[var(--color-muted)]" />
                ) : summary?.byType && Object.keys(summary.byType).length > 0 ? (
                  <DistributionBar data={summary.byType} colorMap={typeColors} />
                ) : (
                  <p className="text-sm text-[var(--color-muted-foreground)]">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Messages (Fuzzy Grouped) */}
          {groupedMessages.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
                    <FileText className="h-5 w-5 text-orange-400" />
                  </div>
                  <div>
                    <CardTitle className="text-[var(--color-foreground)]">Top Messages</CardTitle>
                    <CardDescription>Most frequent log messages (similar messages grouped)</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-[var(--color-border)]">
                  {groupedMessages.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleMessageClick(item)}
                      className="flex w-full items-center justify-between px-6 py-3 hover:bg-[var(--color-muted)]/30 transition-colors text-left group"
                      title="Click to view logs with this message"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="truncate text-sm text-[var(--color-foreground)] group-hover:text-[var(--color-primary)]">
                          {item.message}
                        </span>
                        {item.variants > 1 && (
                          <span className="flex-shrink-0 rounded bg-blue-500/20 px-1.5 py-0.5 text-[10px] font-medium text-blue-400">
                            {item.variants} variants
                          </span>
                        )}
                      </div>
                      <div className="ml-4 flex items-center gap-2">
                        <span className="flex-shrink-0 rounded bg-[var(--color-secondary)] px-2 py-0.5 text-xs font-medium text-[var(--color-muted-foreground)]">
                          {item.count.toLocaleString()}
                        </span>
                        <ExternalLink className="h-3.5 w-3.5 text-[var(--color-muted-foreground)] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                  <Calendar className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-[var(--color-foreground)]">Timeline</CardTitle>
                  <CardDescription>
                    Log volume over time {timeline?.bucketSize ? `(bucket: ${timeline.bucketSize})` : ''}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="h-32 animate-pulse rounded bg-[var(--color-muted)]" />
              ) : timeline && timeline.buckets.length > 0 ? (
                <TooltipProvider delayDuration={0}>
                  {/* Bar chart */}
                  <div className="flex h-32 items-end gap-0.5">
                    {(() => {
                      const maxCount = Math.max(...timeline.buckets.map((b) => b.counts.total));
                      return timeline.buckets.map((bucket, index) => {
                        const height = maxCount > 0 ? (bucket.counts.total / maxCount) * 100 : 0;
                        const hasErrors = (bucket.counts.byLevel?.['error'] || 0) + (bucket.counts.byLevel?.['fatal'] || 0) > 0;
                        const errorCount = (bucket.counts.byLevel?.['error'] || 0) + (bucket.counts.byLevel?.['fatal'] || 0);
                        const warnCount = bucket.counts.byLevel?.['warn'] || 0;

                        return (
                          <Tooltip key={index}>
                            <TooltipTrigger asChild>
                              <div
                                className="group relative flex-1 min-w-[2px] cursor-pointer"
                                style={{ height: `${Math.max(height, bucket.counts.total > 0 ? 4 : 0)}%` }}
                              >
                                <div
                                  className={cn(
                                    'w-full h-full rounded-t transition-all duration-300',
                                    hasErrors
                                      ? 'bg-red-500/70 group-hover:bg-red-500'
                                      : 'bg-blue-500/70 group-hover:bg-blue-500'
                                  )}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <div className="space-y-1.5">
                                <div className="font-medium border-b border-[var(--color-border)] pb-1">
                                  {new Date(bucket.start).toLocaleString()}
                                </div>
                                <div className="text-[var(--color-muted-foreground)]">
                                  Total: <span className="text-[var(--color-foreground)] font-medium">{bucket.counts.total.toLocaleString()}</span> logs
                                </div>
                                {errorCount > 0 && (
                                  <div className="text-red-400">
                                    Errors: {errorCount.toLocaleString()}
                                  </div>
                                )}
                                {warnCount > 0 && (
                                  <div className="text-yellow-400">
                                    Warnings: {warnCount.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        );
                      });
                    })()}
                  </div>
                  <div className="mt-2 flex justify-between text-xs text-[var(--color-muted-foreground)]">
                    <span>{timeline.buckets[0]?.start ? new Date(timeline.buckets[0].start).toLocaleTimeString() : ''}</span>
                    <span className="text-center">
                      Total: {timeline.buckets.reduce((sum, b) => sum + b.counts.total, 0).toLocaleString()} logs
                    </span>
                    <span>
                      {timeline.buckets.at(-1)?.end ? new Date(timeline.buckets.at(-1)!.end).toLocaleTimeString() : ''}
                    </span>
                  </div>
                </TooltipProvider>
              ) : (
                <div className="flex h-32 items-center justify-center text-sm text-[var(--color-muted-foreground)]">
                  No log data in selected time range
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sub-app breakdown with overtime chart */}
          {summary?.bySubApp && Object.keys(summary.bySubApp).length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-500/10">
                    <Activity className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div>
                    <CardTitle className="text-[var(--color-foreground)]">By Sub-App</CardTitle>
                    <CardDescription>Log distribution by sub-application over time</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const subAppColors = [
                    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
                    'bg-cyan-500', 'bg-pink-500', 'bg-yellow-500', 'bg-indigo-500',
                  ];
                  const rawSubAppColors = [
                    'rgb(59, 130, 246)', 'rgb(34, 197, 94)', 'rgb(168, 85, 247)', 'rgb(249, 115, 22)',
                    'rgb(6, 182, 212)', 'rgb(236, 72, 153)', 'rgb(234, 179, 8)', 'rgb(99, 102, 241)',
                  ];
                  const entries = Object.entries(summary.bySubApp).sort((a, b) => b[1] - a[1]);
                  const topSubApps = entries.slice(0, 8).map(([name]) => name);
                  const total = entries.reduce((sum, [, count]) => sum + count, 0);
                  const maxCount = Math.max(...entries.map(([, count]) => count));

                  // Build sub-app color map
                  const subAppColorMap: Record<string, { bg: string; raw: string }> = {};
                  topSubApps.forEach((name, i) => {
                    subAppColorMap[name] = {
                      bg: subAppColors[i % subAppColors.length] ?? '',
                      raw: rawSubAppColors[i % rawSubAppColors.length] ?? '',
                    };
                  });

                  return (
                    <div className="space-y-6">
                      {/* Overtime stacked bar chart */}
                      {timeline && timeline.buckets.length > 0 && (
                        <TooltipProvider delayDuration={0}>
                          <div className="text-xs text-[var(--color-muted-foreground)] mb-2">
                            Activity over time
                          </div>
                          <div className="flex h-24 items-end gap-0.5">
                            {(() => {
                              const maxBucketTotal = Math.max(
                                ...timeline.buckets.map((b) =>
                                  Object.values(b.counts.bySubApp || {}).reduce((sum, c) => sum + c, 0)
                                )
                              );
                              return timeline.buckets.map((bucket, index) => {
                                const bucketSubApps = bucket.counts.bySubApp || {};
                                const bucketTotal = Object.values(bucketSubApps).reduce((sum, c) => sum + c, 0);
                                const heightPercent = maxBucketTotal > 0 ? (bucketTotal / maxBucketTotal) * 100 : 0;

                                // Build stacked segments for top sub-apps
                                const segments: Array<{ name: string; count: number; color: string }> = [];
                                let otherCount = 0;
                                for (const [name, count] of Object.entries(bucketSubApps)) {
                                  if (topSubApps.includes(name)) {
                                    segments.push({ name, count, color: subAppColorMap[name]?.raw || '#6b7280' });
                                  } else {
                                    otherCount += count;
                                  }
                                }
                                // Sort segments to match legend order
                                segments.sort((a, b) => topSubApps.indexOf(a.name) - topSubApps.indexOf(b.name));
                                if (otherCount > 0) {
                                  segments.push({ name: 'Other', count: otherCount, color: '#6b7280' });
                                }

                                return (
                                  <Tooltip key={index}>
                                    <TooltipTrigger asChild>
                                      <div
                                        className="group relative flex-1 min-w-[2px] flex flex-col-reverse cursor-pointer"
                                        style={{ height: `${Math.max(heightPercent, bucketTotal > 0 ? 4 : 0)}%` }}
                                      >
                                        {segments.map((seg, segIdx) => {
                                          const segHeight = bucketTotal > 0 ? (seg.count / bucketTotal) * 100 : 0;
                                          return (
                                            <div
                                              key={segIdx}
                                              className="w-full transition-all duration-300 group-hover:opacity-80"
                                              style={{
                                                height: `${segHeight}%`,
                                                backgroundColor: seg.color,
                                                borderTopLeftRadius: segIdx === segments.length - 1 ? '2px' : '0',
                                                borderTopRightRadius: segIdx === segments.length - 1 ? '2px' : '0',
                                              }}
                                            />
                                          );
                                        })}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <div className="space-y-1.5">
                                        <div className="font-medium border-b border-[var(--color-border)] pb-1">
                                          {new Date(bucket.start).toLocaleString()}
                                        </div>
                                        <div className="text-[var(--color-muted-foreground)]">
                                          Total: <span className="text-[var(--color-foreground)] font-medium">{bucketTotal.toLocaleString()}</span> logs
                                        </div>
                                        {segments.length > 0 && (
                                          <div className="pt-1 space-y-0.5">
                                            {segments.map((seg) => {
                                              const pct = bucketTotal > 0 ? ((seg.count / bucketTotal) * 100).toFixed(1) : '0.0';
                                              return (
                                                <div key={seg.name} className="flex items-center gap-2">
                                                  <div
                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: seg.color }}
                                                  />
                                                  <span className="text-[var(--color-muted-foreground)]">{seg.name}:</span>
                                                  <span className="text-[var(--color-foreground)]">{seg.count.toLocaleString()}</span>
                                                  <span className="text-[var(--color-muted-foreground)]">({pct}%)</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              });
                            })()}
                          </div>
                          <div className="mt-2 flex justify-between text-xs text-[var(--color-muted-foreground)]">
                            <span>{timeline.buckets[0]?.start ? new Date(timeline.buckets[0].start).toLocaleTimeString() : ''}</span>
                            <span>
                              {timeline.buckets.at(-1)?.end ? new Date(timeline.buckets.at(-1)!.end).toLocaleTimeString() : ''}
                            </span>
                          </div>
                        </TooltipProvider>
                      )}

                      {/* Horizontal bar chart (totals) */}
                      <div className="space-y-2">
                        <div className="text-xs text-[var(--color-muted-foreground)] mb-2">
                          Total distribution
                        </div>
                        {entries.slice(0, 8).map(([subApp, count], index) => {
                          const percentage = (count / maxCount) * 100;
                          const color = subAppColors[index % subAppColors.length];
                          return (
                            <div key={subApp} className="group">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="font-medium text-[var(--color-foreground)] truncate max-w-[200px]" title={subApp}>
                                  {subApp}
                                </span>
                                <span className="text-[var(--color-muted-foreground)]">
                                  {count.toLocaleString()} ({((count / total) * 100).toFixed(1)}%)
                                </span>
                              </div>
                              <div
                                className="h-2 w-full rounded-full bg-[var(--color-secondary)]"
                                title={`${subApp}: ${count.toLocaleString()} logs (${((count / total) * 100).toFixed(1)}% of total)`}
                              >
                                <div
                                  className={cn('h-full rounded-full transition-all duration-500', color)}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Totals summary */}
                      <div className="flex items-center justify-between pt-3 border-t border-[var(--color-border)]">
                        <span className="text-sm text-[var(--color-muted-foreground)]">
                          {entries.length} sub-app{entries.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-sm font-medium text-[var(--color-foreground)]">
                          Total: {total.toLocaleString()} logs
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default Stats;
